import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentActionType } from '@/graphql/generated/schemas'
import {
  SEND_TO_ONTIC_BUTTON_PRESS,
  SEND_TO_ONTIC_FAILURE,
  SEND_TO_ONTIC_SUCCESS,
  trackEvent,
} from '@/utils/analytics'

export const useSendIncidentsToOntic = (
  incidentId: string,
  isRebrand = false
) => {
  const colorScheme = useLightMode()
  const { showSuccess, showError } = useToasts({ isRebrand, colorScheme })

  const [createIncidentAction, { loading: isSendingToOntic }] =
    useCreateIncidentActionMutation()

  const isSalesEnv = useFeatureFlag(FeatureFlag.cannedVideo)

  const send = async () => {
    trackEvent(SEND_TO_ONTIC_BUTTON_PRESS, {
      incident_id: incidentId,
    })
    /**
     * isSalesEnv determins if we're in the sales environment or not
     * In the sales environmen, we only mimic sending the incident to Ontic and not actually send it
     */
    if (isSalesEnv) {
      showSuccess('Incident sent to Ontic successfully')
      return
    }
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.SendToOntic,
          },
        },
        refetchQueries: [
          {
            query: IncidentDocument,
            variables: {
              id: incidentId,
            },
          },
        ],
      })

      showSuccess('Incident sent to Ontic successfully')
      trackEvent(SEND_TO_ONTIC_SUCCESS, {
        incident_id: incidentId,
      })
    } catch (e) {
      Sentry.captureException(e)
      showError(e.message)
      trackEvent(SEND_TO_ONTIC_FAILURE, {
        incident_id: incidentId,
      })
    }
  }

  return {
    isSendingToOntic,
    send,
  }
}
